import React from 'react';
import { CompactHeader } from '../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../components/CompactFooter/CompactFooter';
import './legalDoc.scss';
import './publicPage.scss';

interface PublicPageProps {
  /** Page title, shown in the navy banner. */
  title: string;
  /** One line under the title saying what the page is. */
  subtitle?: string;
  /** Bootstrap-icon class for the banner, e.g. "bi-people-fill". */
  icon?: string;
  children: React.ReactNode;
}

/**
 * Shell for the public company pages (About Us, Our Team, Our Expertise, FAQs,
 * Contact Us) and the legal documents.
 *
 * These pages are served by this app rather than linked out to the marketing site, so
 * an investor never leaves our theme. Carrying the same header and footer as the
 * registration flow is what makes them read as part of the platform.
 */
export const PublicPage: React.FC<PublicPageProps> = ({ title, subtitle, icon, children }) => (
  <div className="public-page-shell">
    <CompactHeader />
    <main className="public-page bg-slate-50 py-8">
      <div className="container max-w-5xl mx-auto px-4 space-y-6">
        <div className="bg-[#052f55] p-6 rounded-xl shadow-sm">
          <h1
            className="text-2xl font-bold m-0 flex items-center gap-2 text-white"
            style={{ color: '#ffffff' }}
          >
            {icon && <i className={`bi ${icon} text-red-500`}></i>}
            <span>{title}</span>
          </h1>
          {subtitle && <p className="text-slate-300 text-xs mt-1 mb-0">{subtitle}</p>}
        </div>
        {children}
      </div>
    </main>
    <CompactFooter />
  </div>
);

/** Standard white card used to group a section on these pages. */
export const PublicCard: React.FC<{ title?: string; icon?: string; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-xs">
    {title && (
      <h2 className="font-bold text-[#052f55] text-base mb-3 pb-2 border-b border-slate-100 flex items-center gap-2">
        {icon && <i className={`bi ${icon}`}></i>}
        <span>{title}</span>
      </h2>
    )}
    {children}
  </section>
);

export default PublicPage;
